/**
 * Keyboard-wedge scanners type their payload into whatever has focus and press
 * Enter. That is indistinguishable from a person typing except for the speed,
 * so the burst is what identifies it.
 */

/** Keystrokes further apart than this came from fingers, not a scanner. */
export const WEDGE_GAP_MS = 60

/** Shorter bursts are almost certainly someone hitting Enter on a form. */
export const WEDGE_MIN_LENGTH = 4

export interface WedgeState {
  buffer: string
  lastAt: number
}

export const emptyWedge: WedgeState = { buffer: '', lastAt: 0 }

/**
 * Feed one keystroke to the detector.
 *
 * Returns the next state and, when the burst just ended in Enter, the code.
 */
export function feedKey(
  state: WedgeState,
  key: string,
  at: number,
): { state: WedgeState; code: string | null } {
  const continues = at - state.lastAt <= WEDGE_GAP_MS

  if (key === 'Enter') {
    const code = state.buffer
    const fast = continues && code.length >= WEDGE_MIN_LENGTH
    return { state: emptyWedge, code: fast ? code : null }
  }

  // Modifiers, arrows and the like carry no payload; ignore them without
  // breaking the burst, which would drop the code they arrived in the middle of.
  if (key.length !== 1) return { state, code: null }

  return {
    state: { buffer: continues ? state.buffer + key : key, lastAt: at },
    code: null,
  }
}

/** True while the person is typing into something, where a wedge must keep out. */
export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}
